import { useCallback } from 'react';
import gettext from '@sdk/lang/gettext';
import useSlingApi from '@sdk/api/useSlingApi';
import ToastComponent from '@sdk/common/toast/ToastComponent';
import useToast from '@sdk/common/toast/useToast';
import getErrorForToast from '@sdk/api/utils/getErrorForToast';
import PositionIcon from '@sdk/assets/svg/position-blue.svg';
import PickerPositionItem from '@sdk/components/forms/PickerPositionItem';

import MergeModal from '@web/components/MergeModal/MergeModal';
import type { MergeModalItem } from '@web/components/MergeModal/mergeModal.types';

type Props = {
  isOpen: boolean;
  fromPositions: MergeModalItem[];
  availablePositions: MergeModalItem[];
  onClose: () => void;
  onSubmit: () => void;
};

const PositionMergeModal = ({
  isOpen,
  fromPositions,
  availablePositions,
  onClose,
  onSubmit,
}: Props) => {
  const api = useSlingApi();
  const { showErrorToast, showSuccessToast } = useToast();

  const handleSubmit = useCallback(
    async (fromItems: MergeModalItem[], targetItem: MergeModalItem) => {
      try {
        await api.positions.merge(fromItems, targetItem);
        onSubmit();
        showSuccessToast(gettext('Positions merged'));
      } catch (e) {
        showErrorToast(getErrorForToast(e));
      }
    },
    [api, onSubmit, showSuccessToast, showErrorToast]
  );

  return (
    <>
      <MergeModal
        isOpen={isOpen}
        fromItems={fromPositions}
        toItems={availablePositions}
        onClose={onClose}
        onSubmit={handleSubmit}
        placeholder={gettext('Select position')}
        getModalTitle={(isMultiple) =>
          isMultiple ? gettext('Merge positions') : gettext('Merge position')
        }
        getHeader={(isMultiple) =>
          isMultiple ? gettext('Selected positions') : gettext('Selected position')
        }
        getDescription={(isMultiple) =>
          isMultiple
            ? gettext(
                'Please specify which position the selected positions need to be merged into:'
              )
            : gettext(
                'Please specify which position the selected position needs to be merged into:'
              )
        }
        getConfirmation={(isMultiple) =>
          isMultiple
            ? gettext(
                'All users, shifts, conversations and pages associated with the positions [[items]] and [[lastItem]] will be reassigned to the position [[target]].'
              )
            : gettext(
                'All users, shifts, conversations and pages associated with the position [[lastItem]] will be reassigned to the position [[target]].'
              )
        }
        iconComponent={PositionIcon}
        pickerItemComponent={PickerPositionItem}
        pickerInvalidStateText={gettext('* No matching positions')}
      />
      <ToastComponent />
    </>
  );
};

export default PositionMergeModal;
